import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import {
  ArrowLeft,
  Layers,
  RefreshCw,
  Download,
  FileText,
  FileCode2,
  Folder,
  Radio,
  Eye,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { SpectatorShareDialog } from '@/components/workspace/SpectatorShareDialog'

interface WorkspaceHeaderProps {
  projectName?: string
  projectDatabase?: string
  spectatorCount?: number
  isAdmin?: boolean
  hasProject?: boolean
  onRefresh: () => void
  onExport?: (format: 'ecospold1' | 'csv') => void
  mode?: 'modeling' | 'reports'
}

export function WorkspaceHeader({
  projectName,
  projectDatabase,
  spectatorCount = 0,
  isAdmin = false,
  hasProject = false,
  onRefresh,
  onExport,
  mode = 'modeling',
}: WorkspaceHeaderProps) {
  const [shareOpen, setShareOpen] = useState(false)

  return (
    <header className="h-12 flex items-center justify-between px-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Left: Navigation and current project */}
      <div className="flex items-center gap-3">
        <Link to="/dashboard">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>

        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm hidden sm:inline">Workspace</span>
        </div>

        <nav aria-label="Workspace mode" className="ml-2 flex items-center rounded-md border border-border p-0.5">
          <Link
            to="/workspace"
            aria-current={mode === 'modeling' ? 'page' : undefined}
            className={
              mode === 'modeling'
                ? 'rounded px-2 py-0.5 text-xs font-medium bg-accent text-foreground'
                : 'rounded px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground'
            }
          >
            Modeling
          </Link>
          <Link
            to="/workspace/reports"
            aria-current={mode === 'reports' ? 'page' : undefined}
            className={
              mode === 'reports'
                ? 'rounded px-2 py-0.5 text-xs font-medium bg-accent text-foreground'
                : 'rounded px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground'
            }
          >
            Reports
          </Link>
        </nav>

        {projectName && (
          <>
            <div className="h-6 w-px bg-border mx-1" />
            <div className="flex items-center gap-2">
              <Folder className="w-4 h-4 text-muted-foreground" />
              <div className="hidden sm:block">
                <span className="text-sm font-medium">{projectName}</span>
                {projectDatabase && (
                  <span className="text-xs text-muted-foreground ml-2">{projectDatabase}</span>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-red-500 border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
            onClick={() => setShareOpen(true)}
          >
            <Radio className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Share Live</span>
          </Button>
        )}

        {spectatorCount > 0 && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
            <Eye className="w-3 h-3" />
            {spectatorCount} watching
          </span>
        )}

        <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={onRefresh}>
          <RefreshCw className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 gap-1.5" disabled={!hasProject}>
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Export</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              Choose export format
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onExport?.('csv')} className="gap-2">
              <FileText className="w-4 h-4 text-green-500" />
              <div>
                <div className="text-sm">CSV</div>
                <div className="text-xs text-muted-foreground">Flat exchange table (.csv)</div>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport?.('ecospold1')} className="gap-2">
              <FileCode2 className="w-4 h-4 text-blue-500" />
              <div>
                <div className="text-sm">EcoSpold v1</div>
                <div className="text-xs text-muted-foreground">XML format (.xml)</div>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isAdmin && <SpectatorShareDialog open={shareOpen} onOpenChange={setShareOpen} />}
    </header>
  )
}
